import type { QueryClient } from '@tanstack/react-query'
import type { EpisodeResponse, PlaybackProgressResponse } from '@/api/types'

// Идентификатор вкладки для отсева эха: сервер бродкастит playback-событие во все
// соединения пользователя, включая наше. Живёт в памяти модуля — новая вкладка/перезагрузка
// = новый clientId. randomUUID есть только в secure context (https/localhost); на голом
// http по LAN-адресу (проверка с телефона) — фолбэк, уникальности на вкладку хватает.
export const CLIENT_ID: string =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`

// Ближе к концу, чем на столько секунд, — начинаем эпизод сначала, а не с хвоста.
const RESTART_TAIL_SEC = 10

export function mediaKind(episode: EpisodeResponse): 'audio' | 'video' {
  return episode.mimeType.startsWith('video/') ? 'video' : 'audio'
}

// С какой позиции стартовать эпизод: прослушанный или почти дослушанный — с начала.
export function resolveStartPosition(
  progress: PlaybackProgressResponse | null | undefined,
  duration: number,
): number {
  if (!progress || progress.completed) return 0
  if (Number.isFinite(duration) && duration - progress.position < RESTART_TAIL_SEC) return 0
  return progress.position
}

// Доля прослушанного для полоски на карточке: 0..1 или null — полоску не рисуем.
export function listenedFraction(episode: EpisodeResponse): number | null {
  const progress = episode.progress
  if (!progress) return null
  if (progress.completed) return 1
  if (!episode.duration || progress.position <= 0) return null
  return Math.min(1, progress.position / episode.duration)
}

export function updatedAtMs(progress: { updatedAt: string | null }): number | null {
  if (!progress.updatedAt) return null
  const ms = Date.parse(progress.updatedAt)
  return Number.isNaN(ms) ? null : ms
}

// Момент истечения presigned-ссылки S3 (SigV4: X-Amz-Date + X-Amz-Expires), мс.
// null — ссылка не presigned или формат незнакомый: истечение не определить.
export function presignedExpiresAt(url: string): number | null {
  let params: URLSearchParams
  try {
    params = new URL(url).searchParams
  } catch {
    return null
  }
  const date = params.get('X-Amz-Date')
  const expires = Number(params.get('X-Amz-Expires'))
  const match = date?.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/)
  if (!match || !Number.isFinite(expires) || expires <= 0) return null
  const [, y, mo, d, h, mi, s] = match.map(Number)
  return Date.UTC(y, mo - 1, d, h, mi, s) + expires * 1000
}

// Патч progress одного эпизода в кэше ['episodes'] — НЕ invalidate: рефетч списка
// перегенерировал бы все presigned-ссылки и перерисовал всю сетку карточек. Остальные
// элементы массива сохраняют идентичность → memo-карточки не ре-рендерятся.
export function patchEpisodeProgress(
  queryClient: QueryClient,
  progress: PlaybackProgressResponse,
): void {
  queryClient.setQueryData<EpisodeResponse[]>(['episodes'], (list) => {
    if (!list) return list
    let changed = false
    const next = list.map((episode) => {
      if (episode.id !== progress.episodeId) return episode
      const prev = episode.progress
      if (
        prev &&
        prev.position === progress.position &&
        prev.completed === progress.completed &&
        prev.updatedAt === progress.updatedAt
      ) {
        return episode
      }
      changed = true
      return { ...episode, progress }
    })
    return changed ? next : list
  })
}

// Следующий эпизод для автоплея: дальше по порядку списка (pubDate desc), минуя
// прослушанные. Текущего в списке нет (удалён) или дальше пусто — null.
export function findNextEpisode(
  list: EpisodeResponse[] | undefined,
  currentId: string,
): EpisodeResponse | null {
  if (!list) return null
  const index = list.findIndex((episode) => episode.id === currentId)
  if (index === -1) return null
  return list.slice(index + 1).find((episode) => !episode.progress?.completed) ?? null
}
